import { useCallback, useEffect, useRef, useState } from 'react';

import { convertCardPayCompany } from '@/entities/card';
import { useUserPaymentsQuery } from '@/hooks/common/common.gql.impl';
import { UserPaymentsInfo } from '@/models/common';
import { UserPaymentsQuery } from '@/types/bff/gql-schema';
import { useToast } from 'doctornow-design-system';

const POLLING_INTERVAL_MS = 1000;
const MAX_POLLING_DURATION_MS = 120000;
const REMAINING_TIME_UPDATE_INTERVAL_MS = 100;
const PAYMENT_REGISTRATION_FAILURE_MESSAGE = '결제수단을 등록하는데 실패했습니다';

const parseUserPayments = (data: UserPaymentsQuery): UserPaymentsInfo => {
  const payments = data.userPayments.map((payment) => ({
    canInstallment: payment.canInstallment,
    cardName: payment.cardName,
    cardNumber: payment.cardNumber,
    id: payment.id,
    isApproved: payment.isApproved,
    isDefault: payment.isDefault,
    payCompany: convertCardPayCompany(payment.payCompany),
  }));

  return new UserPaymentsInfo(payments);
};

const structuralSharingUserPayments = (
  oldData: UserPaymentsQuery | undefined,
  newData: UserPaymentsQuery
): UserPaymentsQuery => {
  if (!oldData || !newData) return newData;
  if (!oldData.userPayments || !newData.userPayments) return newData;

  const oldPayments = oldData.userPayments;
  const newPayments = newData.userPayments;

  if (oldPayments.length !== newPayments.length) return newData;

  const hasChanged = !oldPayments.every((oldPayment, index) => oldPayment.id === newPayments[index].id);

  return hasChanged ? newData : oldData;
};

interface UsePollingUserPaymentsOptions {
  enabled?: boolean;
  enablePolling?: boolean;
}

const usePollingUserPayments = (options: UsePollingUserPaymentsOptions = {}) => {
  const { enabled = true, enablePolling = false } = options;
  const { toast } = useToast();
  const [isPolling, setIsPolling] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);

  const startTimeRef = useRef<number | null>(null);
  const elapsedTimeRef = useRef<number>(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const visibilityCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const initialPaymentCountRef = useRef<number>(0);

  const {
    data = new UserPaymentsInfo([]),
    refetch,
    isRefetching,
    isFetched,
  } = useUserPaymentsQuery(
    {},
    {
      enabled: enabled || isPolling,
      refetchInterval: enablePolling && isPolling ? POLLING_INTERVAL_MS : false,
      select: parseUserPayments,
      keepPreviousData: true,
      notifyOnChangeProps: ['data'],
      structuralSharing: structuralSharingUserPayments,
    }
  );

  const clearTimers = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (visibilityCheckIntervalRef.current) {
      clearInterval(visibilityCheckIntervalRef.current);
      visibilityCheckIntervalRef.current = null;
    }
  }, []);

  const stopPolling = useCallback(() => {
    setIsPolling(false);
    setRemainingTime(0);
    startTimeRef.current = null;
    elapsedTimeRef.current = 0;
    initialPaymentCountRef.current = 0;
    clearTimers();
  }, [clearTimers]);

  const startPolling = useCallback(() => {
    if (!enablePolling) return;

    stopPolling();

    const now = Date.now();
    startTimeRef.current = now;
    elapsedTimeRef.current = 0;
    initialPaymentCountRef.current = data.payments.length;
    setIsPolling(true);
    setRemainingTime(MAX_POLLING_DURATION_MS);

    timerRef.current = setTimeout(() => {
      toast({
        text: PAYMENT_REGISTRATION_FAILURE_MESSAGE,
        direction: 'bottom',
      });
      stopPolling();
    }, MAX_POLLING_DURATION_MS);

    visibilityCheckIntervalRef.current = setInterval(() => {
      if (startTimeRef.current && document.visibilityState === 'visible') {
        const elapsed = Date.now() - startTimeRef.current;
        const remaining = Math.max(0, MAX_POLLING_DURATION_MS - elapsed);
        setRemainingTime(remaining);
      }
    }, REMAINING_TIME_UPDATE_INTERVAL_MS);
  }, [enablePolling, stopPolling, toast, data]);

  useEffect(() => {
    if (!enablePolling) return;

    const handleVisibilityChange = () => {
      if (!startTimeRef.current) return;

      if (document.visibilityState === 'hidden') {
        const elapsed = Date.now() - startTimeRef.current;
        elapsedTimeRef.current = elapsed;
        setIsPolling(false);
        clearTimers();
      } else if (document.visibilityState === 'visible') {
        const remainingDuration = MAX_POLLING_DURATION_MS - elapsedTimeRef.current;

        if (remainingDuration <= 0) {
          toast({
            text: PAYMENT_REGISTRATION_FAILURE_MESSAGE,
            direction: 'bottom',
          });
          stopPolling();
          return;
        }

        startTimeRef.current = Date.now() - elapsedTimeRef.current;
        setIsPolling(true);
        setRemainingTime(remainingDuration);

        timerRef.current = setTimeout(() => {
          toast({
            text: PAYMENT_REGISTRATION_FAILURE_MESSAGE,
            direction: 'bottom',
          });
          stopPolling();
        }, remainingDuration);

        visibilityCheckIntervalRef.current = setInterval(() => {
          if (startTimeRef.current && document.visibilityState === 'visible') {
            const elapsed = Date.now() - startTimeRef.current;
            const remaining = Math.max(0, MAX_POLLING_DURATION_MS - elapsed);
            setRemainingTime(remaining);
          }
        }, REMAINING_TIME_UPDATE_INTERVAL_MS);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearTimers();
    };
  }, [enablePolling, clearTimers, stopPolling, toast]);

  useEffect(() => {
    if (!isPolling) return;

    const currentPaymentCount = data.payments.length;
    const hasNewPayment = currentPaymentCount > initialPaymentCountRef.current;

    if (hasNewPayment) {
      stopPolling();
    }
  }, [isPolling, data, stopPolling]);

  return {
    userPaymentsInfo: data,
    refetchUserPayments: refetch,
    isUserPaymentsInfoRefetching: isRefetching,
    isUserPaymentsInfoFetched: isFetched,
    startPollingUserPayments: startPolling,
    stopPollingUserPayments: stopPolling,
    isPollingUserPayments: isPolling,
    remainingTime: Math.ceil(remainingTime / 1000),
  };
};

export default usePollingUserPayments;
